<template>
  <gs-card class="gs-element-list" :data-list="listKey">
    <header class="gs-element-list__head">
      <span class="gs-element-list__icon" aria-hidden="true">
        <gs-icon :name="icon" size="18" />
      </span>
      <span class="gs-element-list__heading">
        <span class="gs-element-list__title">{{ title }}</span>
        <span class="gs-element-list__description">{{ description }}</span>
      </span>
      <gs-button variant="outline" size="sm" data-action="add" @click="$emit('add')">
        <gs-icon name="plus" size="14" />
        <span>{{ addLabel }}</span>
      </gs-button>
    </header>

    <p v-if="!items.length" class="gs-element-list__empty">{{ emptyCopy }}</p>

    <ul v-else class="gs-element-list__rows">
      <li v-for="item in items" :key="item.id" class="gs-element-list__row" data-row>
        <span class="gs-element-list__text">
          <span class="gs-element-list__primary">{{ item.primary || fallback }}</span>
          <span v-if="item.secondary" class="gs-element-list__secondary">{{ item.secondary }}</span>
        </span>
        <span class="gs-element-list__timing">
          <gs-icon name="clock" size="13" />
          <span>{{ item.timing }}</span>
        </span>
        <span class="gs-element-list__actions">
          <gs-button
            variant="ghost"
            size="icon"
            data-action="edit"
            :aria-label="`Edit ${item.primary || fallback}`"
            @click="$emit('edit', item.id)"
          >
            <gs-icon name="edit" size="16" />
          </gs-button>
          <gs-button
            variant="ghost"
            size="icon"
            class="is-destructive"
            data-action="delete"
            :aria-label="`Delete ${item.primary || fallback}`"
            @click="$emit('delete', item.id)"
          >
            <gs-icon name="trash" size="16" />
          </gs-button>
        </span>
      </li>
    </ul>
  </gs-card>
</template>

<script>
export default {
  name: 'ElementList',

  props: {
    title: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    icon: {
      type: String,
      default: 'plus',
    },
    // `{ id, primary, secondary, timing }` — the panel maps a `call_to_actions`
    // entry onto this shape, so nothing about the stored payload leaks in here.
    items: {
      type: Array,
      default: () => [],
    },
    addLabel: {
      type: String,
      default: 'Add',
    },
    emptyCopy: {
      type: String,
      default: 'Nothing here yet.',
    },
    fallback: {
      type: String,
      default: 'Untitled',
    },
    listKey: {
      type: String,
      default: '',
    },
  },
};
</script>

<style scoped>
.gs-element-list {
  padding: 16px 18px;
}

.gs-element-list__head {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.gs-element-list__icon {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  color: var(--gs-muted-foreground);
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-element-list__heading {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.gs-element-list__title {
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 700;
  line-height: 1.3;
}

.gs-element-list__description {
  max-width: 56ch;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  line-height: 1.5;
}

.gs-element-list__empty {
  margin: 12px 0 0;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  line-height: 1.5;
}

.gs-element-list__rows {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 14px 0 0;
  padding: 0;
  list-style: none;
}

.gs-element-list__row {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 10px 12px;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-element-list__text {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.gs-element-list__primary {
  color: var(--gs-foreground);
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.gs-element-list__secondary {
  color: var(--gs-muted-foreground);
  font-size: 12px;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.gs-element-list__timing {
  display: inline-flex;
  flex: 0 0 auto;
  gap: 5px;
  align-items: center;
  padding: 4px 8px;
  color: var(--gs-muted-foreground);
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-sm);
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.gs-element-list__actions {
  display: inline-flex;
  flex: 0 0 auto;
  gap: 4px;
}

@media (max-width: 640px) {
  .gs-element-list__row {
    flex-wrap: wrap;
  }

  .gs-element-list__text {
    flex: 1 1 100%;
  }
}
</style>
